import { useCallback, useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { ThreadPanel } from "../components/ThreadPanel";

type Item = {
  escalation_id: string;
  thread_id: string;
  patient_id: string;
  patient_name: string;
  reason: "patient_request" | "red_flag" | "low_confidence" | "repeated_failure";
  severity: "emergency" | "urgent" | "routine";
  sla_due_at: string;
  routed_to_id: string | null;
  routed_to_name: string | null;
  fell_back: boolean;
  breached: boolean;
  created_at: string;
};

const REASONS: Record<Item["reason"], string> = {
  red_flag: "Red flag",
  patient_request: "Asked for a person",
  low_confidence: "Assistant could not answer",
  repeated_failure: "Unresolved after three turns",
};

/** Minutes left, negative once the deadline has gone. */
function minutesLeft(dueAt: string) {
  return Math.round((new Date(dueAt).getTime() - Date.now()) / 60000);
}

function remaining(item: Item) {
  const left = minutesLeft(item.sla_due_at);
  if (left < 0) return `${Math.abs(left)} min over`;
  if (left < 60) return `${left} min left`;
  return `${Math.floor(left / 60)}h ${left % 60}m left`;
}

/**
 * What is waiting for a reply.
 *
 * Everyone at the clinic sees this list, because a front desk chasing an
 * unanswered escalation is doing their job. Nobody but a doctor opens a thread
 * from it, and the server refuses the rest (MSG-F-021). The rows carry a name,
 * a reason and a clock, and no message text at all.
 *
 * An emergency row is drawn to be found in a glance rather than read in order.
 * A patient whose message tripped the red-flag filter has already been shown
 * the Call 999 directive and told the clinic knows, so what this screen owes
 * them is speed rather than ceremony.
 *
 * It refreshes on its own every twenty seconds. A queue somebody has to reload
 * to trust is a queue they stop trusting.
 */
export function Escalations() {
  const [rows, setRows] = useState<Item[]>([]);
  const [open, setOpen] = useState<Item | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Re-renders the clocks without refetching.
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    const { data, error: failed } = await api.GET("/portal/escalations", {
      params: { header: clinicHeader() },
    });
    setLoading(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setError(null);
    setRows((data ?? []) as Item[]);
  }, []);

  useEffect(() => {
    void load();
    const refresh = setInterval(() => void load(), 20_000);
    const clock = setInterval(() => tick((n) => n + 1), 30_000);
    return () => {
      clearInterval(refresh);
      clearInterval(clock);
    };
  }, [load]);

  const emergencies = rows.filter((r) => r.severity === "emergency");

  return (
    <section className="esc">
      <header className="screen-band">
        <div className="screen-band-photo" aria-hidden="true" />
        <div className="screen-band-words">
          <h1>Needs a reply</h1>
          <p className="screen-lead">
            Conversations the assistant handed over, waiting for a person.
          </p>
        </div>
      </header>

      {/* The count as a statement, not a KPI card. There is one number on this
          screen and it belongs in a sentence. */}
      <p className="esc-count">
        {rows.length === 0 ? (
          "Nothing waiting."
        ) : (
          <>
            <strong>{rows.length}</strong>{" "}
            {rows.length === 1 ? "conversation is" : "conversations are"} waiting
            for a person.
          </>
        )}
      </p>

      <ErrorNote message={error} />

      {emergencies.length > 0 && (
        // Not decoration. A red flag means somebody has been told to call 999,
        // and the clinic's part is to follow up quickly. Drawn to be found
        // rather than read in order.
        <div className="esc-emergency" role="alert">
          <span aria-hidden="true" className="esc-emergency-mark">
            !
          </span>
          <div>
            <strong>
              {emergencies.length === 1
                ? "One patient has been shown the emergency directive."
                : `${emergencies.length} patients have been shown the emergency directive.`}
            </strong>
            <p>
              They were told to call 999 and that the clinic knows. Open the
              conversation and follow up.
            </p>
          </div>
        </div>
      )}

      {loading && rows.length === 0 && <p className="empty">Reading the queue…</p>}

      {!loading && rows.length === 0 && (
        <p className="empty">
          Nobody is waiting. Conversations the assistant hands over appear here.
        </p>
      )}

      {rows.length > 0 && (
        <ul className="esc-list">
          {rows.map((row) => {
            const emergency = row.severity === "emergency";
            return (
              <li
                key={row.escalation_id}
                className={`esc-row${emergency ? " emergency" : ""}${
                  row.breached ? " breached" : ""
                }`}
              >
                <div className="esc-who">
                  <span className="esc-name">{row.patient_name}</span>
                  {/* Red only for a red flag. The other three reasons are
                      operational, and four red badges is no badge at all. */}
                  <span className={`esc-why why-${row.reason}`}>
                    {REASONS[row.reason]}
                  </span>
                </div>

                <div className="esc-routed">
                  <span className="esc-label">Routed to</span>
                  {row.fell_back ? (
                    <span>
                      Anyone{row.routed_to_name ? ` (was ${row.routed_to_name})` : ""}
                    </span>
                  ) : (
                    <span className={row.routed_to_name ? undefined : "quiet"}>
                      {row.routed_to_name ?? "Nobody yet"}
                    </span>
                  )}
                </div>

                <div className={`esc-clock${row.breached ? " over" : ""}`}>
                  <span className="esc-label">Waiting</span>
                  <span className="esc-time">{remaining(row)}</span>
                </div>

                <button type="button" onClick={() => setOpen(row)}>
                  Open
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {open && (
        <ThreadPanel
          threadId={open.thread_id}
          patientId={open.patient_id}
          patientName={open.patient_name}
          onClose={() => setOpen(null)}
          onChanged={() => void load()}
        />
      )}
    </section>
  );
}
